import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function ExportButton({
  getExportData,
  label = "Export",
  disabled = false,
  dropUp = false,
}) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const containerRef = useRef(null);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  const loadData = async () => {
    if (typeof getExportData !== "function") {
      alert("Export service is not configured.");
      return null;
    }

    try {
      setLoading(true);

      const response = await getExportData();

      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.leads)
            ? response.leads
            : [];

      if (data.length === 0) {
        alert("No data to export.");
        return null;
      }

      return data;
    } catch (err) {
      console.error("Failed to load export data:", err);

      alert(err?.response?.data?.error || "Failed to load export data");

      return null;
    } finally {
      setLoading(false);
    }
  };

  const getContactName = (lead) => {
    const contact = Array.isArray(lead?.contacts) ? lead.contacts[0] : null;

    if (!contact) {
      return "";
    }

    return [
      contact.first_name || contact.firstName,
      contact.last_name || contact.lastName,
    ]
      .filter(Boolean)
      .join(" ");
  };

  const getDmsProviders = (lead) => {
    if (Array.isArray(lead?.dms_providers)) {
      return lead.dms_providers;
    }

    if (Array.isArray(lead?.dmsProviders)) {
      return lead.dmsProviders;
    }

    if (typeof lead?.dms_providers === "string") {
      try {
        const parsed = JSON.parse(lead.dms_providers);

        return Array.isArray(parsed) ? parsed : [lead.dms_providers];
      } catch {
        return [lead.dms_providers];
      }
    }

    return [];
  };

  const getProducts = (lead) => {
    if (Array.isArray(lead?.products)) {
      return lead.products;
    }

    if (typeof lead?.products === "string") {
      try {
        const parsed = JSON.parse(lead.products);

        return Array.isArray(parsed) ? parsed : [lead.products];
      } catch {
        return [lead.products];
      }
    }

    return [];
  };

  const getManufacturers = (lead) => {
    if (Array.isArray(lead?.manufacturer)) {
      return lead.manufacturer;
    }

    if (Array.isArray(lead?.manufacturers)) {
      return lead.manufacturers;
    }

    return lead?.manufacturer ? [lead.manufacturer] : [];
  };

  const formatDate = (value) => {
    if (!value) {
      return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleDateString();
  };

  const buildExcelRows = (data) =>
    data.map((lead) => ({
      "Lead ID": lead.lead_id || "",
      Company: lead.company || "",
      Contact: getContactName(lead),
      Email: lead.email || "",
      "Sales Rep": lead.partner_name || lead.sales_rep_name || "",
      "Lead Source": lead.lead_source || lead.leadSource || "",
      "Lead Type": lead.lead_type || lead.leadType || "",
      "Service Dashboard":
        lead.service_dashboard || lead.serviceDashboard || "",
      "Contact Date": lead.contact_date || lead.contactDate || "",
      Opportunity: lead.opportunity_yn || lead.opportunityYn || "",
      Status: lead.status || "",
      "DMS Providers": getDmsProviders(lead).join(", "),
      "Other DMS": lead.other_dms_provider || lead.otherDmsProvider || "",
      Products: getProducts(lead).join(", "),
      "Dealer Group": lead.dealer_group || lead.dealerGroup || "",
      Manufacturers: getManufacturers(lead).join(", "),
      Rooftops: lead.rooftops ?? "",
      Website: lead.website || "",
      City: lead.city || "",
      State: lead.state || "",
      ZIP: lead.zip_code || lead.zipCode || "",
      Country: lead.country || "",
      "Demo Status": lead.demo_status || lead.demoStatus || "Not Booked",
      "Demo Date": lead.demo_date || lead.demoDate || "",
      "Demo Time": lead.demo_time || lead.demoTime || "",
      "Demo Timezone": lead.demo_timezone || "",
      "Microsoft Booking ID":
        lead.microsoft_booking_id || lead.microsoftBookingId || "",
      "Created Date": formatDate(lead.created_at),
      "Updated Date": formatDate(lead.updated_at),
      Notes: lead.notes || "",
    }));

  const exportToExcel = async () => {
    setOpen(false);

    const data = await loadData();

    if (!data) {
      return;
    }

    try {
      setLoading(true);

      const rows = buildExcelRows(data);

      const worksheet = XLSX.utils.json_to_sheet(rows);

      worksheet["!cols"] = [
        { wch: 12 },
        { wch: 30 },
        { wch: 25 },
        { wch: 30 },
        { wch: 25 },
        { wch: 20 },
        { wch: 20 },
        { wch: 25 },
        { wch: 18 },
        { wch: 15 },
        { wch: 20 },
        { wch: 30 },
        { wch: 25 },
        { wch: 35 },
        { wch: 20 },
        { wch: 30 },
        { wch: 12 },
        { wch: 30 },
        { wch: 18 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 25 },
        { wch: 20 },
        { wch: 40 },
      ];

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

      XLSX.writeFile(
        workbook,
        `leads_export_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } catch (err) {
      console.error("Excel export failed:", err);

      alert("Failed to export Excel file");
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    setOpen(false);

    const data = await loadData();

    if (!data) {
      return;
    }

    try {
      setLoading(true);

      const doc = new jsPDF("landscape", "mm", "a4");

      doc.setFontSize(16);
      doc.text("Leads Export", 14, 20);

      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);

      doc.text(`Total Leads: ${data.length}`, 14, 33);

      const tableData = data.map((lead) => [
        lead.company || "",
        getContactName(lead),
        lead.status || "",
        getDmsProviders(lead).join(", "),
        lead.demo_status || lead.demoStatus || "Not Booked",
      ]);

      autoTable(doc, {
        head: [
          ["Company", "Contact", "Status", "DMS Providers", "Demo Status"],
        ],
        body: tableData,
        startY: 38,
        styles: {
          fontSize: 8,
        },
        margin: {
          left: 14,
          right: 14,
        },
      });

      doc.save(`leads_export_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);

      alert("Failed to export PDF");
    } finally {
      setLoading(false);
    }
  };

  // In ExportButton.js - Update the render section

  return (
    <div
      ref={containerRef}
      className={`export-wrapper ${dropUp ? "export-wrapper-dropup" : ""}`}
    >
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        disabled={loading || disabled}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <i
          className={loading ? "fas fa-spinner fa-spin" : "fas fa-download"}
        ></i>
        {loading ? "Exporting..." : label}
        <i
          className={`fas fa-chevron-${open ? "up" : "down"}`}
          style={{ marginLeft: "4px", fontSize: "0.6rem" }}
        ></i>
      </button>

      {open && !loading && (
        <div className="export-dropdown-menu" role="menu">
          <button
            type="button"
            onClick={exportToExcel}
            className="export-menu-item"
            role="menuitem"
          >
            <i className="fas fa-file-excel"></i>
            <span>Excel (.xlsx)</span>
          </button>
          <button
            type="button"
            onClick={exportToPDF}
            className="export-menu-item"
            role="menuitem"
          >
            <i className="fas fa-file-pdf"></i>
            <span>PDF</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default ExportButton;
